import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign, ShoppingCart } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const AverageCartValueCalculator = () => {
  const [totalRevenue, setTotalRevenue] = useState(100000);
  const [totalCarts, setTotalCarts] = useState(2000);
  const [averageCartValue, setAverageCartValue] = useState(0);

  useEffect(() => {
    calculateAverageCartValue();
  }, [totalRevenue, totalCarts]);

  const calculateAverageCartValue = () => {
    const acv = totalCarts > 0 ? totalRevenue / totalCarts : 0;
    setAverageCartValue(parseFloat(acv.toFixed(2)));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/average-cart-value-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Average Cart Value Calculator: Optimize Shopping Cart Value | Free Tool</title>
        <meta name="description" content="Calculate your Average Cart Value instantly. Learn how to increase cart values, reduce abandonment rates, and optimize your e-commerce performance." />
        <meta name="keywords" content="average cart value calculator, shopping cart value calculator, e-commerce metrics, cart optimization tool, retail calculator" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Average Cart Value Calculator: Boost E-commerce Performance" />
        <meta property="og:description" content="Calculate your Average Cart Value instantly. Get actionable insights to increase cart values and optimize your online store." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Average Cart Value Calculator: Boost E-commerce Performance" />
        <meta property="twitter:description" content="Calculate your Average Cart Value instantly. Get actionable insights to increase cart values and optimize your online store." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Average Cart Value Calculator",
            "description": "Free calculator to measure and optimize shopping cart values",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate Average Cart Value",
              "Compare with Industry Benchmarks",
              "Get Cart Optimization Tips",
              "Track E-commerce Performance",
              "Monitor Shopping Behavior"
            ],
            "about": {
              "@type": "Thing",
              "name": "Average Cart Value",
              "description": "The average value of items in shopping carts across all sessions, including both completed and abandoned carts."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "E-commerce Managers, Online Retailers, Digital Marketers"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Average Cart Value",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter your total revenue"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input total number of shopping carts"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your calculated average cart value and optimization tips"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Average Cart Value Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Revenue</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={totalRevenue}
                  onChange={(e) => setTotalRevenue(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="100000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Number of Carts</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <ShoppingCart className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={totalCarts}
                  onChange={(e) => setTotalCarts(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="2000"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            ${averageCartValue}
            <ShoppingCart className="w-8 h-8 ml-2 text-indigo-400" />
          </div>
          <p className="text-gray-600">Average Cart Value</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Industry Benchmarks</h2>
        <p className="text-gray-600">
          Your Average Cart Value is ${averageCartValue}. This means that, on average, each shopping cart (including both completed and abandoned carts) contains items worth ${averageCartValue}. A higher average cart value can indicate effective upselling and cross-selling strategies.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Industry Benchmarks</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>Fashion E-commerce: $80-120</li>
          <li>Electronics: $200-250</li>
          <li>Home & Garden: $120-180</li>
          <li>Beauty & Cosmetics: $40-60</li>
          <li>Grocery & Food: $70-100</li>
          <li>Luxury Retail: $300+</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Tips to Increase Average Cart Value</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Offer product bundles or complementary items</li>
          <li>Implement a minimum order value for free shipping</li>
          <li>Use dynamic product recommendations</li>
          <li>Create tiered pricing or volume discounts</li>
          <li>Offer limited-time promotions or discounts on cart totals</li>
          <li>Implement a loyalty program that encourages larger purchases</li>
          <li>Show savings on bulk purchases</li>
          <li>Add urgency with time-limited offers</li>
          <li>Display "frequently bought together" suggestions</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Key Performance Indicators</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Relationship with Cart Abandonment Rate</li>
          <li>Impact on Overall Revenue</li>
          <li>Correlation with Customer Lifetime Value</li>
          <li>Effect on Profit Margins</li>
          <li>Influence on Marketing ROI</li>
        </ul>
      </div>
    </div>
  );
};

export default AverageCartValueCalculator;
